import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GroupedTableVirtuoso } from 'react-virtuoso';
import {
  Table, TableRow, TableCell, Button, Typography, Box,
  useTheme,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import { applyRoundAndFormat } from '../../../utils';
import { MagicWand01 } from '../../../assets';
import OrderFormRow from './OrderFormRow';
import { OrderForm, OrderFormUpdate, OrderFormItem, OrderFormCategory, DeliveryDates, OrderFormItemUpdate } from '../../../app/services/api/orderManagementClient';
import { TFunction } from 'i18next';
import { PredictedDemandModel } from '../../../app/services/api/podClient';
import { isOrderCategory, OrderCategoriesTranslationMap } from '../../../enums/OrderCategories';

interface VirtualizedTableProps {
  loading: boolean;
  showImage: boolean;
  orderForm?: OrderForm;
  orderType: number;
  displayedDates: DeliveryDates[];
  predictedDemands: PredictedDemandModel[];
  matchPredictedQuantities: (dateIndex: number) => void;
  handleMagicWandClicked: (predictedQuantity: number, item: OrderFormItem, index: number) => void;
  canEditPredictions: boolean;
  orderFormUpdates: OrderFormUpdate;
  handleSingleQuantityChange: (
    newQuantity: number | null,
    sku: string,
    itemId: number,
    dateIndex: number,
  ) => Promise<void>;
  canSaveOrder: boolean;
  isQuantityError: (item: OrderFormItem, currentValue: string) => boolean;
  quantityError: (item: OrderFormItem, currentValue: string) => string | null;
  showMessageWhenNoCost: boolean;
  currencyCode: string;
  t: TFunction;
  isLargeScreen: boolean;
  usePagination: boolean;
  page: number;
  totalPages: number;
  handlePageChange: (newPage: number) => void;
  dirtyRef: React.MutableRefObject<boolean>;
  minimumOrderValue?: number;
  isEnhancedCutoffEnabled: boolean;
  isParEnabled?: boolean;
}

type CutoffCountdownTextProps = {
  targetTimeMs: number | null;
  rawCutoffTime: string;
  isEnhancedCutoffEnabled: boolean;
  expiredLabel: string;
};

const CutoffCountdownText = React.memo(({
  targetTimeMs,
  rawCutoffTime,
  isEnhancedCutoffEnabled,
  expiredLabel,
}: CutoffCountdownTextProps) => {
  const [currentTimeMs, setCurrentTimeMs] = useState(() => Date.now());

  useEffect(() => {
    if (!isEnhancedCutoffEnabled || targetTimeMs == null) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setCurrentTimeMs(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, [isEnhancedCutoffEnabled, targetTimeMs]);

  const displayValue = useMemo(() => {
    if (!isEnhancedCutoffEnabled || targetTimeMs == null) {
      return rawCutoffTime;
    }

    const remainingMs = targetTimeMs - currentTimeMs;

    if (remainingMs <= 0) {
      return expiredLabel;
    }

    const totalMinutes = Math.ceil(remainingMs / (60 * 1000));
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;
    const formattedParts = [];

    if (days > 0) {
      formattedParts.push(`${days}d`);
    }

    if (hours > 0 || days > 0) {
      formattedParts.push(`${hours}h`);
    }

    formattedParts.push(`${minutes}m`);

    return formattedParts.join(' ');
  }, [currentTimeMs, expiredLabel, isEnhancedCutoffEnabled, rawCutoffTime, targetTimeMs]);

  return (
    <Typography component="span"
      display="block"
      sx={{ fontSize: '12px', textDecoration: isEnhancedCutoffEnabled ? 'underline dotted' : 'none', cursor: isEnhancedCutoffEnabled ? 'help' : 'default' }}>
      {displayValue}
    </Typography>
  );
});

CutoffCountdownText.displayName = 'CutoffCountdownText';

const VirtualizedTable: React.FC<VirtualizedTableProps> = ({
  loading,
  showImage,
  orderForm,
  orderType,
  displayedDates,
  predictedDemands,
  matchPredictedQuantities,
  handleMagicWandClicked,
  canEditPredictions,
  orderFormUpdates,
  handleSingleQuantityChange,
  canSaveOrder,
  isQuantityError,
  quantityError,
  showMessageWhenNoCost,
  currencyCode,
  t,
  isLargeScreen,
  dirtyRef,
  minimumOrderValue = 0,
  isEnhancedCutoffEnabled,
  isParEnabled = false,
}) => {
  const updatesRef = useRef<OrderFormItemUpdate[]>([]);
  const theme = useTheme();

  const parseCutoffDurationMs = useCallback((cutOffTime: string): number | null => {
    if (!cutOffTime) {
      return null;
    }

    const parts = cutOffTime.split(':').map(Number);

    if (parts.some((value) => Number.isNaN(value))) {
      return null;
    }

    if (parts.length === 3) {
      const [days, hours, minutes] = parts;
      return ((((days * 24) + hours) * 60) + minutes) * 60 * 1000;
    }

    if (parts.length === 2) {
      const [hours, minutes] = parts;
      return ((hours * 60) + minutes) * 60 * 1000;
    }

    return null;
  }, []);

  const cutoffTargets = useMemo(() => {
    if (!isEnhancedCutoffEnabled) {
      return displayedDates.map(() => null);
    }

    const capturedNow = Date.now();
    return displayedDates.map((item) => {
      const durationMs = parseCutoffDurationMs(item.cutOffTime);
      return durationMs == null ? null : capturedNow + durationMs;
    });
  }, [displayedDates, isEnhancedCutoffEnabled, parseCutoffDurationMs]);

  const parseDeliveryDate = useCallback((deliveryDate: string): dayjs.Dayjs => {
    if (/^\d{2}-\d{2}-\d{4}$/.test(deliveryDate)) {
      const [day, month, year] = deliveryDate.split('-').map(Number);
      return dayjs(new Date(year, month - 1, day));
    }

    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(deliveryDate)) {
      const [month, day, year] = deliveryDate.split('/').map(Number);
      return dayjs(new Date(year, month - 1, day));
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(deliveryDate)) {
      const [year, month, day] = deliveryDate.split('-').map(Number);
      return dayjs(new Date(year, month - 1, day));
    }

    return dayjs(deliveryDate);
  }, []);

  const getCutoffTooltipLabel = useCallback((targetTimeMs: number | null): string => {
    if (!isEnhancedCutoffEnabled || targetTimeMs == null) {
      return '';
    }

    return dayjs(targetTimeMs).format('DD-MM-YYYY (ddd)');
  }, [isEnhancedCutoffEnabled]);

  const getDeliveryDateLabel = useCallback((deliveryDate: string): string => {
    if (!isEnhancedCutoffEnabled) {
      return deliveryDate;
    }

    const parsedDate = parseDeliveryDate(deliveryDate);
    if (!parsedDate.isValid()) {
      return deliveryDate;
    }

    return parsedDate.format('DD-MM-YYYY (ddd)');
  }, [isEnhancedCutoffEnabled, parseDeliveryDate]);

  const mergeUpdates = (target: OrderFormItemUpdate, incoming: OrderFormItemUpdate): OrderFormItemUpdate => {
    // merge dates with incoming taking precedence
    const acc: Record<string, number | null> = {};
    for (const d of target.dates ?? []) acc[d.date] = d.quantity;
    for (const d of incoming.dates ?? []) acc[d.date] = d.quantity;

    const dates = Object.keys(acc)
      .sort()
      .map(date => ({ date, quantity: acc[date] }));

    return {
      ...target,
      ...incoming,
      id: target.id ?? incoming.id,
      sku: target.sku ?? incoming.sku,
      dates,
    };
  };

  const upsertOrderFormItemUpdate = (single: OrderFormItemUpdate) => {
    dirtyRef.current = true;
    const arr = updatesRef.current;
    const idx = arr.findIndex(it =>
      (single.id && it.id === single.id) || (single.sku && it.sku === single.sku),
    );
    if (idx === -1) {
      updatesRef.current = [...arr, {
        ...single,
        dates: Object.keys(Object.assign({}, ...single.dates.map(d => ({ [d.date]: d.quantity }))))
          .sort()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map(date => ({ date, quantity: (Object.assign({}, ...single.dates.map(d => ({ [d.date]: d.quantity }))) as any)[date] })),
      }];
      return;
    }
    const next = arr.slice();
    next[idx] = mergeUpdates(arr[idx], single);
    updatesRef.current = next;
  };

  const getItemUpdate = useCallback((item: OrderFormItem) => {
    const arr = updatesRef.current;
    return arr.find(u => (u.id && u.id === item.id) || (u.sku && u.sku === item.sku));
  }, []);

  const flattenedItems = useMemo(() => {
    return orderForm?.categories?.flatMap(c => c.items) ?? [];
  }, [orderForm?.categories]);

  const columnTotals = useMemo(() => {
    if (!orderForm?.availableDates) return [];
    return orderForm.availableDates.map((_, dateIndex) => {
      return flattenedItems.reduce((sum, item) => {
        const updatedQty = orderFormUpdates.items
          .find((updateItem) => updateItem.id === item.id)
          ?.dates.find((date) => date.date === orderForm.availableDates[dateIndex].deliveryDate)?.quantity;
        const qty = updatedQty != null
          ? updatedQty
          : item.quantity[dateIndex] ?? (isParEnabled
            ? item.parQuantityApplied?.[dateIndex] ?? item.calculatedParQuantity ?? 0
            : 0);
        const price = item.price ?? 0;
        return sum + (qty * price);
      }, 0);
    });
  }, [flattenedItems, isParEnabled, orderForm?.availableDates, orderFormUpdates.items]);

  if (loading) {
    return null;
  }

  const categories: OrderFormCategory[] = orderForm?.categories ?? [];
  // Each group = one category, groupCounts holds number of item rows per category
  const groupCounts = categories.map(c => c.items?.length ?? 0);

  return (
    <GroupedTableVirtuoso
      style={{
        height: 'max(95vh, 400px)',
        transform: 'none', // <— prevents Virtuoso wrapper from creating a new stacking context
        border: '0px',
      }}
      overscan={20}
      groupCounts={groupCounts}
      components={{
        Table: (props) => (
          <Table
            {...props}
            size="small"
            sx={{
              tableLayout: 'auto',
              minWidth: 900,
              border: '0px',
            }}
            aria-label="order form table"
          />
        ),
        TableRow: TableRow,
      }}
      fixedHeaderContent={() => (
        <TableRow sx={{
          borderBottom: '10px', margin: 0, position: 'sticky',
          top: 0,
        }}>
          <TableCell
            sx={{
              width: '40%',
              maxWidth: '40%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
            }}
          >
            {t('productInformation')}
          </TableCell>
          {displayedDates.map((item, index) => {
            const cutoffTargetTimeMs = cutoffTargets[index];
            const cutoffTooltipLabel = getCutoffTooltipLabel(cutoffTargetTimeMs);

            return (
              <TableCell
                key={index}
                align="center"
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: theme.palette.custom.gray[200],
                }}
              >
                <Box>
                  {predictedDemands.length > 0 && canEditPredictions &&
                    <Button
                      fullWidth
                      variant="primary"
                      disabled={loading}
                      startIcon={<MagicWand01 style={{ width: 20, height: 20 }} />}
                      size="md"
                      style={{ fontSize: 12 }}
                      onClick={() => matchPredictedQuantities(index)}
                    >
                      {t('applyAI')}
                    </Button>
                  }
                  <Typography component="span"
                    sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                    {t('deliveryDate')}
                  </Typography>
                  <Typography component="span"
                    display="block"
                    sx={{ fontSize: '12px' }}>
                    {getDeliveryDateLabel(item.deliveryDate)}
                  </Typography>
                  <Typography component="span"
                    display="block"
                    sx={{ fontWeight: 'bold', mt: 1, fontSize: '12px' }}>
                    {t('cutOff')}
                  </Typography>
                  <Tooltip
                    title={cutoffTooltipLabel}
                    arrow
                    disableHoverListener={!isEnhancedCutoffEnabled || cutoffTooltipLabel === ''}
                  >
                    <span>
                      <CutoffCountdownText
                        targetTimeMs={cutoffTargetTimeMs}
                        rawCutoffTime={item.cutOffTime}
                        isEnhancedCutoffEnabled={isEnhancedCutoffEnabled}
                        expiredLabel={t('cutOffPassed')}
                      />
                    </span>
                  </Tooltip>
                  {columnTotals.length > 0 && (
                    <Box sx={{ mt: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                      <Typography
                        component="span"
                        display="block"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '11px',
                          color: minimumOrderValue > 0
                            ? (columnTotals[index] >= minimumOrderValue ? 'success.main' : 'error.main')
                            : 'text.primary',
                        }}
                      >
                        {applyRoundAndFormat(columnTotals[index], currencyCode)}
                        {minimumOrderValue > 0 && (
                          <>
                            {' / '}
                            {applyRoundAndFormat(minimumOrderValue, currencyCode)}
                          </>
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TableCell>
            );
          })}
        </TableRow>
      )}
      groupContent={(groupIndex) => {
        const category = categories[groupIndex];
        return (
          <TableCell
            key={`category-${orderType}-${category.id}`}
            colSpan={displayedDates.length + 1}
            sx={{
              backgroundColor: theme.palette.primary.main,
              px: 3,
              py: 2,
              position: 'sticky',
              top: 38,
              zIndex: 1,
            }}
          >
            <Typography variant="textSM"
              color="white">
              {isOrderCategory(category.name)
                ? t(
                  OrderCategoriesTranslationMap[
                  category.name as keyof typeof OrderCategoriesTranslationMap
                  ],
                )
                : category.name}
            </Typography>
          </TableCell>
        );
      }}
      itemContent={(itemIndex, groupIndex) => {
        const category = categories[groupIndex];
        const offset = groupCounts.slice(0, groupIndex).reduce((a, b) => a + b, 0);
        const item = category.items?.[itemIndex - offset];
        if (!item) {
          return null;
        }

        return (
          <OrderFormRow
            key={`categoryItem-${orderType}-${category.id}-${item.id}`}
            orderType={orderType}
            item={item}
            dates={displayedDates}
            getItemUpdate={getItemUpdate}
            handleSingleQuantityChange={handleSingleQuantityChange}
            canSaveOrder={canSaveOrder}
            isQuantityError={isQuantityError}
            quantityError={quantityError}
            showMessageWhenNoCost={showMessageWhenNoCost}
            currencyCode={currencyCode}
            t={t}
            predictedDemands={predictedDemands}
            handleMagicWandClicked={(predictedQty, itm, idx) => handleMagicWandClicked(predictedQty, itm, idx)}
            canEditPredictions={canEditPredictions}
            upsertOrderFormItemUpdate={upsertOrderFormItemUpdate}
            isAlternateItemCardEnabled={true}
            isParEnabled={isParEnabled}
          />
        );
      }}
    />
  );
};

export default VirtualizedTable;